import { useEffect, useRef } from 'react';
import useReducedMotion from '../../hooks/useReducedMotion';
import useIsDark from '../../hooks/useIsDark';

const TRAIL_SPAWN_DISTANCE = 6;
const MAX_TRAIL_PARTICLES = 200;

export default function CustomCursor() {
    const canvasRef = useRef(null);
    const reducedMotion = useReducedMotion();
    const { isDarkRef } = useIsDark();

    useEffect(() => {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (isTouchDevice) return;

        // --- Cursor dot & circle (DOM) ---
        const customCursor = document.createElement('div');
        customCursor.className = 'custom-cursor';
        document.body.appendChild(customCursor);

        const cursorCircle = document.createElement('div');
        cursorCircle.className = 'cursor-circle';
        document.body.appendChild(cursorCircle);

        // If reduced motion, only show the cursor dot — no trail canvas
        if (reducedMotion) {
            const onMouseMove = (e) => {
                customCursor.style.left = e.clientX + 'px';
                customCursor.style.top = e.clientY + 'px';
                cursorCircle.style.left = e.clientX + 'px';
                cursorCircle.style.top = e.clientY + 'px';
            };
            const onMouseLeave = () => { customCursor.style.opacity = '0'; cursorCircle.style.opacity = '0'; };
            const onMouseEnter = () => { customCursor.style.opacity = '1'; };
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseleave', onMouseLeave);
            document.addEventListener('mouseenter', onMouseEnter);
            return () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseleave', onMouseLeave);
                document.removeEventListener('mouseenter', onMouseEnter);
                customCursor.remove();
                cursorCircle.remove();
            };
        }

        // --- Trail canvas ---
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        // Mouse state
        let mouseX = window.innerWidth / 2;
        let mouseY = window.innerHeight / 2;
        let lastTrailX = mouseX;
        let lastTrailY = mouseY;

        // Trail: array of { x, y, size, opacity, vx, vy, life, maxLife }
        const trail = [];

        customCursor.style.left = mouseX + 'px';
        customCursor.style.top = mouseY + 'px';
        cursorCircle.style.left = mouseX + 'px';
        cursorCircle.style.top = mouseY + 'px';

        const onMouseMove = (e) => {
            mouseX = e.clientX;
            mouseY = e.clientY;
            customCursor.style.left = mouseX + 'px';
            customCursor.style.top = mouseY + 'px';
            cursorCircle.style.left = mouseX + 'px';
            cursorCircle.style.top = mouseY + 'px';

            // Spawn trail particles at intervals along the path
            const dx = mouseX - lastTrailX;
            const dy = mouseY - lastTrailY;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist >= TRAIL_SPAWN_DISTANCE) {
                const steps = Math.floor(dist / TRAIL_SPAWN_DISTANCE);
                for (let i = 0; i < steps; i++) {
                    const t = (i + 1) / steps;
                    const px = lastTrailX + dx * t;
                    const py = lastTrailY + dy * t;
                    spawnParticle(px, py, dx, dy, dist);
                }
                lastTrailX = mouseX;
                lastTrailY = mouseY;
            }
        };

        const spawnParticle = (x, y, dx, dy, speed) => {
            // If at cap, remove oldest particle to make room
            if (trail.length >= MAX_TRAIL_PARTICLES) {
                trail.shift();
            }
            // Drift perpendicular to movement + slight random
            const angle = Math.atan2(dy, dx);
            const spread = (Math.random() - 0.5) * 1.2;
            const perpAngle = angle + Math.PI / 2;
            const driftSpeed = 0.15 + Math.random() * 0.25;
            const maxLife = 70 + Math.random() * 40;

            trail.push({
                x: x + Math.cos(perpAngle) * spread * 8,
                y: y + Math.sin(perpAngle) * spread * 8,
                size: Math.random() * 1.8 + 0.6,
                vx: Math.cos(perpAngle) * driftSpeed * spread + (Math.random() - 0.5) * 0.1,
                vy: Math.sin(perpAngle) * driftSpeed * spread + (Math.random() - 0.5) * 0.1,
                life: 0,
                maxLife,
                hue: Math.random() * 360,
            });
        };

        const burst = [];

        // Interactive hover
        const onMouseEnterInteractive = () => {
            customCursor.style.width = '6px';
            customCursor.style.height = '6px';
            cursorCircle.style.transition = 'none';
            cursorCircle.style.width = '10px';
            cursorCircle.style.height = '10px';
            void cursorCircle.offsetWidth;
            cursorCircle.style.transition = 'width 0.3s ease-out, height 0.3s ease-out, opacity 0.3s';
            cursorCircle.style.width = '30px';
            cursorCircle.style.height = '30px';
            cursorCircle.style.opacity = '1';
        };

        const onMouseLeaveInteractive = () => {
            customCursor.style.width = '10px';
            customCursor.style.height = '10px';
            cursorCircle.style.width = '10px';
            cursorCircle.style.height = '10px';
            cursorCircle.style.opacity = '0';
        };

        // Ripple effect on click (skip buttons and form elements)
        const onClickRipple = (e) => {
            const target = e.currentTarget;
            const tag = target.tagName.toLowerCase();
            if (tag === 'button' || tag === 'input' || tag === 'textarea' || tag === 'select') return;
            const rect = target.getBoundingClientRect();
            const ripple = document.createElement('span');
            const size = Math.max(rect.width, rect.height) * 2;
            ripple.style.cssText = `
                position: absolute;
                border-radius: 50%;
                width: ${size}px;
                height: ${size}px;
                left: ${e.clientX - rect.left - size / 2}px;
                top: ${e.clientY - rect.top - size / 2}px;
                background: currentColor;
                opacity: 0.06;
                transform: scale(0);
                animation: cursorRipple 0.6s ease-out forwards;
                pointer-events: none;
                z-index: 0;
            `;
            // Ensure target has positioning context for the absolute ripple
            const computed = window.getComputedStyle(target);
            if (computed.position === 'static') {
                target.style.position = 'relative';
            }
            target.appendChild(ripple);
            ripple.addEventListener('animationend', () => ripple.remove());
        };

        // Inject ripple keyframes once
        if (!document.getElementById('ripple-keyframes')) {
            const style = document.createElement('style');
            style.id = 'ripple-keyframes';
            style.textContent = `@keyframes cursorRipple { to { transform: scale(1); opacity: 0; } }`;
            document.head.appendChild(style);
        }

        document.addEventListener('mousemove', onMouseMove);

        const trackedElements = new Set();
        const attachListeners = () => {
            const interactiveElements = document.querySelectorAll('a, button, input, textarea, select, [role="button"]');
            interactiveElements.forEach((el) => {
                if (trackedElements.has(el)) return;
                trackedElements.add(el);
                el.addEventListener('mouseenter', onMouseEnterInteractive);
                el.addEventListener('mouseleave', onMouseLeaveInteractive);
                el.addEventListener('click', onClickRipple);
            });
        };
        attachListeners();
        const observer = new MutationObserver(attachListeners);
        observer.observe(document.body, { childList: true, subtree: true });

        // Visibility
        const onMouseLeave = () => {
            customCursor.style.opacity = '0';
            cursorCircle.style.opacity = '0';
        };
        const onMouseEnter = () => {
            customCursor.style.opacity = '1';
        };
        document.addEventListener('mouseleave', onMouseLeave);
        document.addEventListener('mouseenter', onMouseEnter);

        // --- Animation loop ---
        let raf;
        let wasBubbleHover = false;
        const animate = () => {
            if (document.hidden) {
                raf = requestAnimationFrame(animate);
                return;
            }
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Check if hovering a bubble (set by FilmGrain)
            const isBubbleHover = document.body.dataset.bubbleHover === '1';
            if (isBubbleHover !== wasBubbleHover) {
                wasBubbleHover = isBubbleHover;
                if (isBubbleHover) {
                    onMouseEnterInteractive();
                } else {
                    onMouseLeaveInteractive();
                }
            }

            const color = isDarkRef.current ? '255, 255, 255' : '0, 0, 0';

            // Update & draw trail particles
            for (let i = trail.length - 1; i >= 0; i--) {
                const p = trail[i];

                // Particles far from cursor age faster
                const distToCursor = Math.sqrt((p.x - mouseX) ** 2 + (p.y - mouseY) ** 2);
                const agingSpeed = distToCursor < 60 ? 1 : 1 + (distToCursor - 60) / 80;
                p.life += agingSpeed;

                p.x += p.vx;
                p.y += p.vy;
                p.vx *= 0.98;
                p.vy *= 0.98;

                if (p.life >= p.maxLife) {
                    trail.splice(i, 1);
                    continue;
                }

                const progress = p.life / p.maxLife;
                const alpha = progress < 0.1
                    ? (progress / 0.1) * 0.35
                    : 0.35 * (1 - (progress - 0.1) / 0.9);

                // Slight twinkle
                const twinkle = 0.85 + Math.sin(p.life * 0.3 + p.x * 0.01) * 0.15;
                const finalAlpha = alpha * twinkle;

                // Draw glow
                const glowSize = p.size * 3;
                const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowSize);
                if (isDarkRef.current) {
                    gradient.addColorStop(0, `rgba(${color}, ${finalAlpha * 0.4})`);
                    gradient.addColorStop(1, `rgba(${color}, 0)`);
                } else {
                    gradient.addColorStop(0, `hsla(${p.hue}, 80%, 70%, ${finalAlpha * 0.5})`);
                    gradient.addColorStop(1, `hsla(${p.hue}, 80%, 70%, 0)`);
                }
                ctx.beginPath();
                ctx.arc(p.x, p.y, glowSize, 0, Math.PI * 2);
                ctx.fillStyle = gradient;
                ctx.fill();

                // Draw star dot
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = isDarkRef.current
                    ? `rgba(${color}, ${finalAlpha})`
                    : `hsla(${p.hue}, 75%, 65%, ${finalAlpha})`;
                ctx.fill();
            }

            // Update & draw burst particles
            for (let i = burst.length - 1; i >= 0; i--) {
                const p = burst[i];
                p.life++;
                p.x += p.vx;
                p.y += p.vy;
                p.vx *= 0.96;
                p.vy *= 0.96;

                if (p.life >= p.maxLife) {
                    burst.splice(i, 1);
                    continue;
                }

                const progress = p.life / p.maxLife;
                const alpha = (1 - progress) * 0.6;
                const size = p.size * (1 - progress * 0.5);

                const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size * 2.5);
                gradient.addColorStop(0, `rgba(${color}, ${alpha * 0.5})`);
                gradient.addColorStop(1, `rgba(${color}, 0)`);
                ctx.beginPath();
                ctx.arc(p.x, p.y, size * 2.5, 0, Math.PI * 2);
                ctx.fillStyle = gradient;
                ctx.fill();

                ctx.beginPath();
                ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${color}, ${alpha})`;
                ctx.fill();
            }

            // Draw faint connections between close trail particles (constellation effect)
            for (let i = 0; i < trail.length; i++) {
                for (let j = i + 1; j < trail.length; j++) {
                    const a = trail[i];
                    const b = trail[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < 50) {
                        const progressA = a.life / a.maxLife;
                        const progressB = b.life / b.maxLife;
                        const alphaA = progressA < 0.1 ? progressA / 0.1 : 1 - (progressA - 0.1) / 0.9;
                        const alphaB = progressB < 0.1 ? progressB / 0.1 : 1 - (progressB - 0.1) / 0.9;
                        const lineAlpha = (1 - dist / 50) * 0.08 * Math.min(alphaA, alphaB);
                        const avgHue = (a.hue + b.hue) / 2;

                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.strokeStyle = isDarkRef.current
                            ? `rgba(${color}, ${lineAlpha})`
                            : `hsla(${avgHue}, 70%, 70%, ${lineAlpha * 1.5})`;
                        ctx.lineWidth = 0.5;
                        ctx.stroke();
                    }
                }
            }

            raf = requestAnimationFrame(animate);
        };
        raf = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseleave', onMouseLeave);
            document.removeEventListener('mouseenter', onMouseEnter);
            observer.disconnect();
            trackedElements.forEach((el) => {
                el.removeEventListener('mouseenter', onMouseEnterInteractive);
                el.removeEventListener('mouseleave', onMouseLeaveInteractive);
                el.removeEventListener('click', onClickRipple);
            });
            trackedElements.clear();
            customCursor.remove();
            cursorCircle.remove();
        };
    }, [reducedMotion, isDarkRef]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 1,
            }}
        />
    );
}
